import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import Spinner from 'components/common/Spinner';
import Axios from 'utils/Axios';
import { logOut } from 'reduxStore/appStatic/actions';

const HomePage = lazy(() => import('containers/Home'));
const MealPlansPage = lazy(() => import('containers/MealPlans'));
const MealPlanPage = lazy(() => import('containers/MealPlan'));
const AboutUsPage = lazy(() => import('containers/AboutUsPage'));
const FaqPage = lazy(() => import('containers/Faq'));
const WeeklyMeals = lazy(() => import('containers/WeeklyMeals'));
const Error = lazy(() => import('containers/Error'));
const OrderPlanZipCode = lazy(() => import('pages/OrderPlan/ZipCodePage'));
const OrderPlanCustomize = lazy(() => import('pages/OrderPlan/CustomizePage'));
const OrderPlanMeals = lazy(() => import('pages/OrderPlan/MealsPage'));
const OrderPlanCart = lazy(() => import('pages/OrderPlan/CartPage'));
const OrderPlanCheckout = lazy(() => import('pages/OrderPlan/CheckoutPage'));
const OrderPlanConfirmation = lazy(
  () => import('pages/OrderPlan/ConfirmationPage')
);
const NewsDetailPage = lazy(() => import('containers/NewsDetailPage'));
const TermsPage = lazy(() => import('containers/Terms'));
const PrivacyPolicyPage = lazy(() => import('containers/PrivacyPolicyPage'));
const LoginPage = lazy(() => import('containers/Auth/Login'));
const ForgotPage = lazy(() => import('containers/Auth/ForgotPassword'));
const SetPassword = lazy(() => import('containers/Auth/SetPassword'));
const OrderMealZipCode = lazy(() => import('pages/OrderMeal/ZipCodePage'));
const OrderMealMeals = lazy(() => import('pages/OrderMeal/MealsPage'));
const OrderMealCart = lazy(() => import('pages/OrderMeal/CartPage'));
const OrderMealCheckout = lazy(() => import('pages/OrderMeal/CheckoutPage'));
const OrderMealConfirmation = lazy(
  () => import('pages/OrderMeal/ConfirmationPage')
);
const Contacts = lazy(() => import('containers/Contacts'));
const GiftCard = lazy(() => import('containers/GiftCard'));
const UnderConstruction = lazy(() => import('containers/UnderConstruction'));
const Profile = lazy(() => import('containers/Profile'));
const Certificates = lazy(() => import('components/Certificates'));

const redirectIfLoggedIn = ['/login', '/forgot-password', '/password'];

// Wrapper component to handle authentication redirects
const ProtectedRoute = ({ user, children, path }) => {
  if (redirectIfLoggedIn.includes(path) && !isEmpty(user)) {
    return <Navigate to="/profile" replace />;
  }
  return <Suspense fallback={<Spinner />}>{children}</Suspense>;
};

const mapStateToProps = state => ({
  user: state.appStatic.user,
});
const AppRoute = connect(mapStateToProps)(ProtectedRoute);

class AppRouter extends React.Component {
  constructor(props) {
    super(props);
    if (!isEmpty(props.user)) {
      Axios.get('/user?include=addresses')
        .then(() => {})
        .catch(() => this.props.logOut());
    }
  }

  render() {
    const { user } = this.props;
    return (
      <Routes>
        <Route
          path="/"
          element={
            <AppRoute user={user} path="/">
              <HomePage />
            </AppRoute>
          }
        />
        <Route
          path="/meal-plans"
          element={
            <AppRoute user={user} path="/meal-plans">
              <MealPlansPage />
            </AppRoute>
          }
        />
        <Route
          path="/meal-plan/:id"
          element={
            <AppRoute user={user} path="/meal-plan/:id">
              <MealPlanPage />
            </AppRoute>
          }
        />
        <Route
          path="/about-us"
          element={
            <AppRoute user={user} path="/about-us">
              <AboutUsPage />
            </AppRoute>
          }
        />
        <Route
          path="/weekly-meals"
          element={
            <AppRoute user={user} path="/weekly-meals">
              <WeeklyMeals />
            </AppRoute>
          }
        />
        <Route
          path="/order-plan/zip-code"
          element={
            <AppRoute user={user} path="/order-plan/zip-code">
              <OrderPlanZipCode />
            </AppRoute>
          }
        />
        <Route
          path="/order-plan/customize"
          element={
            <AppRoute user={user} path="/order-plan/customize">
              <OrderPlanCustomize />
            </AppRoute>
          }
        />
        <Route
          path="/order-plan/meals"
          element={
            <AppRoute user={user} path="/order-plan/meals">
              <OrderPlanMeals />
            </AppRoute>
          }
        />
        <Route
          path="/order-plan/cart"
          element={
            <AppRoute user={user} path="/order-plan/cart">
              <OrderPlanCart />
            </AppRoute>
          }
        />
        <Route
          path="/order-plan/checkout"
          element={
            <AppRoute user={user} path="/order-plan/checkout">
              <OrderPlanCheckout />
            </AppRoute>
          }
        />
        <Route
          path="/order-plan/confirmation"
          element={
            <AppRoute user={user} path="/order-plan/confirmation">
              <OrderPlanConfirmation />
            </AppRoute>
          }
        />
        <Route
          path="/order-plan"
          element={<Navigate to="/order-plan/zip-code" replace />}
        />
        <Route
          path="/faq"
          element={
            <AppRoute user={user} path="/faq">
              <FaqPage />
            </AppRoute>
          }
        />
        <Route
          path="/news"
          element={
            <AppRoute user={user} path="/news">
              <UnderConstruction />
            </AppRoute>
          }
        />
        <Route
          path="/news-detail"
          element={
            <AppRoute user={user} path="/news-detail">
              <NewsDetailPage />
            </AppRoute>
          }
        />
        <Route
          path="/terms"
          element={
            <AppRoute user={user} path="/terms">
              <TermsPage />
            </AppRoute>
          }
        />
        <Route
          path="/login"
          element={
            <AppRoute user={user} path="/login">
              <LoginPage />
            </AppRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <AppRoute user={user} path="/forgot-password">
              <ForgotPage />
            </AppRoute>
          }
        />
        <Route
          path="/password"
          element={
            <AppRoute user={user} path="/password">
              <SetPassword />
            </AppRoute>
          }
        />
        <Route
          path="/order-meal/zip-code"
          element={
            <AppRoute user={user} path="/order-meal/zip-code">
              <OrderMealZipCode />
            </AppRoute>
          }
        />
        <Route
          path="/order-meal/meals"
          element={
            <AppRoute user={user} path="/order-meal/meals">
              <OrderMealMeals />
            </AppRoute>
          }
        />
        <Route
          path="/order-meal/cart"
          element={
            <AppRoute user={user} path="/order-meal/cart">
              <OrderMealCart />
            </AppRoute>
          }
        />
        <Route
          path="/order-meal/checkout"
          element={
            <AppRoute user={user} path="/order-meal/checkout">
              <OrderMealCheckout />
            </AppRoute>
          }
        />
        <Route
          path="/order-meal/confirmation"
          element={
            <AppRoute user={user} path="/order-meal/confirmation">
              <OrderMealConfirmation />
            </AppRoute>
          }
        />
        <Route
          path="/order-meal"
          element={<Navigate to="/order-meal/zip-code" replace />}
        />
        <Route
          path="/contact-us"
          element={
            <AppRoute user={user} path="/contact-us">
              <Contacts />
            </AppRoute>
          }
        />
        <Route
          path="/gift-card"
          element={
            <AppRoute user={user} path="/gift-card">
              <GiftCard />
            </AppRoute>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <AppRoute user={user} path="/privacy-policy">
              <PrivacyPolicyPage />
            </AppRoute>
          }
        />
        <Route
          path="/certificates"
          element={
            <AppRoute user={user} path="/certificates">
              <Certificates />
            </AppRoute>
          }
        />
        <Route
          path="/profile/*"
          element={
            <AppRoute user={user} path="/profile/*">
              <Profile />
            </AppRoute>
          }
        />
        <Route
          path="*"
          element={
            <AppRoute user={user} path="*">
              <Error />
            </AppRoute>
          }
        />
      </Routes>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  logOut: () => dispatch(logOut()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AppRouter);
